import { useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { ArrowRightIcon, ShieldCheckIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ThemeSwitch } from '@/app/theme-switch';
import { useSession } from '@/app/session';
import { ApiError } from '@/lib/api';
import { useT } from '@/lib/i18n';

/**
 * Sign in — the kit's `login/v1` layout (two columns: brand panel on the start
 * side, form on the end side; single column below `lg`).
 *
 * This is the console's START SCREEN: an unauthenticated visitor to any route
 * is redirected here by `AuthGuard`, and Sign out returns here.
 *
 * As of Phase 4 this screen AUTHENTICATES: it calls `POST /auth/login`, and
 * navigates only on success. A failure shows the server's message and the
 * console stays closed. The Phase-0 stub that accepted any address — and the
 * banner that admitted it — are gone.
 *
 * LANGUAGE: this screen is the one place the console cannot honour the
 * operator's preference, because it does not yet know who they are — a display
 * language is an account property and the account is what is being proved. It
 * therefore renders in the default (`en-AE`) and switches on the first render
 * after sign-in. Guessing from `navigator.language` would be worse: it would
 * show whatever the browser was installed with rather than the language the
 * operator chose.
 *
 * Theming: the kit's v1 puts a photograph in the left half. It is replaced by a
 * panel built from the active preset's own tokens (`--primary`, `--sidebar`,
 * `--chart-*`), so the login screen re-skins with the console instead of
 * sitting outside it — Ocean Breeze being the default preset, that is what it
 * shows out of the box. A bundled photo would have looked identical under all
 * eight presets, and in dark mode.
 */
export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, pending } = useSession();
  const t = useT();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Where AuthGuard bounced them from, so a deep link survives the detour.
  const from = (location.state as { from?: string } | null)?.from ?? '/';

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    try {
      await signIn(email, password);
      // Only on success — the console must not open on a failed sign-in.
      navigate(from, { replace: true });
    } catch (err) {
      // The server sends one message for every credential failure, on purpose:
      // saying which half was wrong would turn this form into an account
      // enumerator. Show it as-is rather than inventing a friendlier one.
      setError(err instanceof ApiError ? err.message : t('login.unreachable'));
    }
  }

  return (
    <div className="bg-background flex min-h-screen flex-col lg:flex-row">
      {/* Brand panel — hidden below lg exactly as the kit's v1 hides its image. */}
      <aside
        className="from-primary via-primary to-chart-2 text-primary-foreground relative hidden w-1/2 flex-col justify-between overflow-hidden bg-linear-to-br p-10 lg:flex"
        aria-hidden="true"
      >
        <div className="bg-primary-foreground/10 pointer-events-none absolute -top-24 -inset-e-24 size-96 rounded-full blur-3xl" />
        <div className="bg-primary-foreground/10 pointer-events-none absolute -bottom-32 -inset-s-16 size-80 rounded-full blur-3xl" />

        <div className="relative flex items-center gap-3">
          <div className="bg-primary-foreground/15 flex size-10 items-center justify-center rounded-xl backdrop-blur">
            <ShieldCheckIcon className="size-5" />
          </div>
          <div className="leading-tight">
            <p className="font-semibold">{t('chrome.brand')}</p>
            <p className="text-primary-foreground/70 text-xs">{t('login.brandSub')}</p>
          </div>
        </div>

        <div className="relative max-w-md">
          <h2 className="text-3xl font-bold tracking-tight">{t('login.headline')}</h2>
          <p className="text-primary-foreground/80 mt-4 text-sm leading-relaxed">
            {t('login.blurb')}
          </p>
        </div>

        <p className="text-primary-foreground/70 relative text-xs">{t('login.footer')}</p>
      </aside>

      {/* Form column. */}
      <div className="flex w-full flex-col lg:w-1/2">
        <div className="flex justify-end p-4">
          <ThemeSwitch />
        </div>

        <div className="flex flex-1 items-center justify-center px-4 pb-16">
          <div className="w-full max-w-md space-y-8">
            <div className="text-center">
              {/* The brand mark the aside carries, for the single-column layout. */}
              <div className="bg-primary text-primary-foreground mx-auto mb-4 flex size-11 items-center justify-center rounded-xl lg:hidden">
                <ShieldCheckIcon className="size-5" />
              </div>
              <h1 className="text-3xl font-bold tracking-tight">{t('login.title')}</h1>
              <p className="text-muted-foreground mt-2 text-sm">{t('login.subtitle')}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <Alert variant="destructive" role="alert">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-4">
                <div className="grid gap-2">
                  <Label htmlFor="email">{t('login.email')}</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="admin@etcc.ae"
                    autoComplete="username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="password">{t('login.password')}</Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    required
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  {/* X6: this sat beside the label as "Forgot password?" — a
                      dead <span> styled like an action, offering a flow that
                      does not exist. There is no reset route, deliberately:
                      `users.ts` refuses to let one operator set another's
                      password, because that would let an admin take a
                      colleague's account silently. So it states what actually
                      recovers an account, below the field, as help text. */}
                  <p className="text-muted-foreground text-xs">{t('login.forgot')}</p>
                </div>
              </div>

              <Button type="submit" className="w-full" disabled={pending}>
                {pending ? t('login.signingIn') : t('login.signIn')}
                <ArrowRightIcon className="size-4 rtl:rotate-180" />
              </Button>
            </form>

            {/* The kit's v1 offers Google / GitHub here. Neither is a NAFIS
                identity provider, and which provider authenticates an operator
                is deployment configuration (`AUTH_MODE`, PROGRESS §28) — a fixed
                pair of buttons would contradict the seam that exists. */}
            <p className="text-muted-foreground text-center text-xs">{t('login.ssoNote')}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
